// ============================================================================
// LIRE LE FICHIER D'UN FOURNISSEUR — le tableau brut, avant toute interprétation.
//
// ⚠️ CE MODULE NE COMPREND RIEN AU MÉTIER : il rend des lignes de cellules, point. Ce qui est un
// gencode, un prix ou une taille se décide à l'étape du masque, avec l'humain devant. Mélanger les
// deux ferait deviner des colonnes, et un import qui devine se trompe en silence.
//
// ⚠️ IL EST PUR, DONC ÉPROUVABLE SANS NAVIGATEUR. C'est la partie qui casse — séparateurs, guillemets,
// accents — et celle qu'aucun rendu ne montre : un fichier mal lu produit des lignes plausibles.
//
// ⚠️ DEUX PIÈGES PROPRES AUX EXPORTS FRANÇAIS, et ils sont la raison d'être de ce fichier :
//   · le SÉPARATEUR est le point-virgule, parce qu'Excel FR l'impose (la virgule est décimale) ;
//   · l'ENCODAGE est souvent Windows-1252, pas UTF-8 — lu en UTF-8, « Fenêtre » devient « FenÃªtre »,
//     et personne ne s'en aperçoit avant de voir les libellés en caisse.
// ============================================================================

/** Ce qu'une lecture rend : des lignes de cellules, et ce qu'on a dû deviner pour y arriver. */
import type { CellObject } from 'xlsx';
import { t } from '../i18n';
export interface FichierLu {
  rows: string[][];
  /**
   * Le NUMÉRO DE LIGNE du tableur de chaque ligne rendue — celui que l'opérateur voit dans Excel.
   * ⚠️ LES LIGNES VIDES SONT RETIRÉES DE `rows`, jamais de la numérotation : une ligne vide sous l'en-tête décalait
   *    sinon tous les « L37 » du rapport, et l'opérateur corrigeait la mauvaise ligne.
   */
  lines: number[];
  /**
   * Le séparateur retenu — montré à l'écran, parce qu'il se DEVINE et qu'un devin doit s'annoncer.
   * ⚠️ `null` pour un classeur : il n'y en a pas, et en afficher un serait une information inventée.
   */
  separateur: ';' | ',' | '\t' | null;
  /** L'encodage retenu, même raison — `null` pour un classeur, que la bibliothèque décode elle-même. */
  encodage: 'utf-8' | 'windows-1252' | null;
  /** Les feuilles du classeur, dans leur ordre. Vide pour un CSV. */
  sheets: string[];
  /** La feuille LUE — `null` pour un CSV. L'étape suivante permettra d'en changer. */
  sheet: string | null;
}

/**
 * LE SÉPARATEUR D'UN FICHIER — celui qui découpe le plus RÉGULIÈREMENT, pas le plus fréquent.
 *
 * ⚠️ COMPTER LES OCCURRENCES NE SUFFIT PAS : un fichier à prix français (« 12,50 ») porte plus de
 * virgules que de points-virgules, et serait découpé au mauvais caractère — chaque prix éclaté en deux
 * colonnes. On regarde donc quel séparateur donne le MÊME nombre de colonnes sur les premières lignes :
 * c'est la propriété d'un vrai séparateur, pas d'un caractère de contenu.
 */
export function detecterSeparateur(texte: string): ';' | ',' | '\t' {
  const rows = texte.split(/\r?\n/).filter((l) => l.trim() !== '').slice(0, 12);
  if (rows.length === 0) return ';';

  let meilleur: ';' | ',' | '\t' = ';';
  let meilleurScore = -1;
  for (const sep of [';', ',', '\t'] as const) {
    const comptes = rows.map((l) => decouper(l, sep).length);
    const colonnes = comptes[0]!;
    if (colonnes < 2) continue;
    // Régularité : combien de lignes ont EXACTEMENT le même nombre de colonnes que la première.
    const reguliers = comptes.filter((n) => n === colonnes).length;
    // ⚠️ La régularité prime, le nombre de colonnes départage : à régularité égale, le séparateur qui
    // découpe le plus est le bon (une virgule décimale donne 2 colonnes très régulières, elle aussi).
    const score = reguliers * 1000 + colonnes;
    if (score > meilleurScore) { meilleurScore = score; meilleur = sep; }
  }
  return meilleur;
}

/**
 * Découpe UNE ligne, en respectant les guillemets.
 * ⚠️ `split(sep)` NE MARCHE PAS : « "Veste, noire";29,90 » est un libellé et un prix, pas trois
 * colonnes. Les guillemets sont la seule façon dont un fichier dit « ce séparateur est du texte ».
 */
function decouper(line: string, sep: string): string[] {
  const out: string[] = [];
  let current = '';
  let dansGuillemets = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i]!;
    if (c === '"') {
      // Un guillemet DOUBLÉ à l'intérieur d'un champ cité est un guillemet littéral.
      if (dansGuillemets && line[i + 1] === '"') { current += '"'; i++; continue; }
      dansGuillemets = !dansGuillemets;
      continue;
    }
    if (c === sep && !dansGuillemets) { out.push(current); current = ''; continue; }
    current += c;
  }
  out.push(current);
  return out.map((x) => x.trim());
}

/**
 * Le texte d'un fichier, dans le bon encodage.
 *
 * ⚠️ ON ESSAIE L'UTF-8 EN MODE STRICT D'ABORD : décodé en Windows-1252, un fichier UTF-8 rend des
 * accents doublés, ce qui NE LÈVE PAS. L'inverse, lui, lève — et c'est ce que `fatal: true` exploite :
 * un octet invalide en UTF-8 est la preuve qu'on n'est pas en UTF-8, alors qu'aucun octet n'est
 * invalide en Windows-1252 (les 256 sont définis). L'ordre n'est donc pas interchangeable.
 */
export function decoder(octets: ArrayBuffer): { texte: string; encodage: 'utf-8' | 'windows-1252' } {
  try {
    const texte = new TextDecoder('utf-8', { fatal: true }).decode(octets);
    return { texte, encodage: 'utf-8' };
  } catch {
    return { texte: new TextDecoder('windows-1252').decode(octets), encodage: 'windows-1252' };
  }
}

/**
 * Lit un CSV complet — lignes, séparateur et encodage.
 *
 * ⚠️ ON PARCOURT TOUT LE TEXTE, on ne découpe PAS en lignes d’abord. Une cellule citée peut contenir
 * un RETOUR À LA LIGNE — une adresse, une composition textile sur deux lignes — et découper avant
 * d’avoir lu les guillemets casserait cet article en deux lignes bancales : la première amputée de ses
 * colonnes de droite, la seconde décalée. Rien ne lèverait ; l’import créerait deux articles faux.
 * (Constaté en relisant l’analyseur de la maquette, qui parcourt bien le texte entier.)
 *
 * ⚠️ LES LIGNES ENTIÈREMENT VIDES SONT ÉCARTÉES, mais pas les lignes à cellules vides : un fichier
 * finit régulièrement par un saut de ligne, et une ligne fantôme se retrouverait à l’écran comme un
 * article sans nom que personne n’a saisi.
 * ⚠️ LE BOM EST RETIRÉ : laissé en place, il colle au premier en-tête, qui cesse alors de
 * correspondre au masque enregistré la fois d’avant.
 */
export function lireCsv(octets: ArrayBuffer): FichierLu {
  const { texte, encodage } = decoder(octets);
  const sansBom = texte.charCodeAt(0) === 0xfeff ? texte.slice(1) : texte;
  const separateur = detecterSeparateur(sansBom);

  const rows: string[][] = [];
  let line: string[] = [];
  let cellule = '';
  let dansGuillemets = false;
  const finLigne = () => { line.push(cellule); rows.push(line); line = []; cellule = ''; };
  // ⚠️ Le numéro d'un enregistrement est son RANG (un champ cité peut porter un retour à la ligne) : c'est celui que
  //    le tableur affiche en ouvrant le fichier.

  for (let i = 0; i < sansBom.length; i++) {
    const c = sansBom[i]!;
    if (dansGuillemets) {
      // Un guillemet DOUBLÉ à l’intérieur d’un champ cité est un guillemet littéral.
      if (c === '"' && sansBom[i + 1] === '"') { cellule += '"'; i++; continue; }
      if (c === '"') { dansGuillemets = false; continue; }
      cellule += c;
      continue;
    }
    if (c === '"') { dansGuillemets = true; continue; }
    if (c === separateur) { line.push(cellule); cellule = ''; continue; }
    if (c === '\n') { finLigne(); continue; }
    if (c === '\r') continue;
    cellule += c;
  }
  if (cellule !== '' || line.length > 0) finLigne();

  const kept: string[][] = [];
  const lines: number[] = [];
  rows.forEach((r, i) => {
    const cells = r.map((x) => x.trim());
    if (cells.some((x) => x !== '')) { kept.push(cells); lines.push(i + 1); }
  });
  return { rows: kept, lines, separateur, encodage, sheets: [], sheet: null };
}

/** Un nombre écrit en entier, sans exposant ni bruit de flottant (« 3400000000012 », « 12.3456 », « 5.5 »). */
const nombreBrut = (v: number): string => {
  const r = Math.round(v * 1e10) / 1e10;
  return Number.isInteger(r) ? r.toFixed(0) : String(r);
};

/**
 * LA VALEUR D'UNE CELLULE, PAS SON AFFICHAGE — sauf pour une date, dont l'affichage EST la valeur lisible.
 *
 * ⚠️ L'AFFICHAGE D'EXCEL DÉFORME LES NOMBRES, et sans un mot : un code-barres au format Standard s'affiche
 *    « 3.4E+12 » (toutes les lignes écartées pour code mal formé), un prix de 1 250 au format milliers s'affiche
 *    « 1,250 » (écrit 1,25 €), un coût de 12,3456 au format 0.00 s'affiche « 12.35 » (l'échelle fine du prix d'achat
 *    perdue). On lit donc le NOMBRE stocké.
 * ⚠️ UN POURCENTAGE est stocké en fraction (5,5 % = 0,055) : ramené en points, comme on l'écrit à la main.
 */
export function valeurDeCellule(cell: CellObject | undefined, estUneDate: (format: string) => boolean): string {
  if (!cell) return '';
  if (cell.t === 'n' && typeof cell.v === 'number') {
    const format = typeof cell.z === 'string' ? cell.z : '';
    if (format && estUneDate(format)) return String(cell.w ?? '').trim();
    if (format.includes('%')) return nombreBrut(cell.v * 100);
    return nombreBrut(cell.v);
  }
  if (cell.t === 's') return String(cell.v ?? '').trim();
  return String(cell.w ?? cell.v ?? '').trim();
}
/**
 * LIRE UN CLASSEUR .XLSX / .XLS — cellule par cellule, sur toute la plage de la feuille.
 *
 * ⚠️ LA VALEUR, PAS L'AFFICHAGE (`valeurDeCellule`) : l'affichage d'Excel arrondit et met en forme les nombres.
 * ⚠️ DES TABLEAUX DE CELLULES, pas des objets nommés d'après l'en-tête : la correspondance colonne → champ se décide à
 *    l'étape du masque, avec l'humain devant — laisser la bibliothèque nommer les colonnes reviendrait à deviner.
 * ⚠️ UNE CELLULE VIDE GARDE SA PLACE : sans elle, les colonnes de droite remonteraient d'un cran, et la taille d'une
 *    ligne à prix manquant glisserait dans la colonne du prix.
 * ⚠️ LA PREMIÈRE FEUILLE PAR DÉFAUT, mais toutes sont rendues : un classeur fournisseur porte souvent un onglet
 *    « Lisez-moi » en tête, et lire la première sans le dire importerait une notice.
 */
export async function lireClasseur(octets: ArrayBuffer, feuille?: string): Promise<FichierLu> {
  const XLSX = await import('xlsx');
  // ⚠️ `cellNF` GARDE LE FORMAT de chaque cellule : c'est lui qui dit qu'un nombre est une date ou un pourcentage.
  const wb = XLSX.read(octets, { type: 'array', cellNF: true });
  const sheets = [...wb.SheetNames];
  if (sheets.length === 0) throw new Error(t('Ce classeur ne contient aucune feuille.'));

  const nom = feuille && sheets.includes(feuille) ? feuille : sheets[0]!;
  const grid = wb.Sheets[nom];
  if (!grid) throw new Error(t('La feuille « {nom} » est illisible.', { nom }));

  const rows: string[][] = [];
  const lines: number[] = [];
  const ref = grid['!ref'];
  if (ref) {
    const plage = XLSX.utils.decode_range(ref);
    for (let r = plage.s.r; r <= plage.e.r; r += 1) {
      const cells: string[] = [];
      for (let c = plage.s.c; c <= plage.e.c; c += 1) {
        cells.push(valeurDeCellule(grid[XLSX.utils.encode_cell({ r, c })], (format) => XLSX.SSF.is_date(format)));
      }
      // ⚠️ LE NUMÉRO EST CELUI DU TABLEUR (base 0 ici, base 1 à l'écran), plage décalée et lignes vides comprises.
      if (cells.some((c) => c !== '')) { rows.push(cells); lines.push(r + 1); }
    }
  }
  return { rows, lines, separateur: null, encodage: null, sheets, sheet: nom };
}

/** L'extension dit quel lecteur employer — c'est la seule chose sur laquelle on peut se fier avant d'ouvrir. */
export function estClasseur(nom: string): boolean {
  return /\.(xlsx|xlsm|xlsb|xls)$/i.test(nom);
}

/**
 * LE FICHIER PORTE-T-IL UNE LIGNE D'EN-TÊTE ?
 *
 * ⚠️ ON PROPOSE, ON N'IMPOSE PAS : l'écran laisse corriger. Un fichier dont la première ligne est déjà
 * une donnée perdrait sinon son premier article, silencieusement — c'est la perte la plus difficile à
 * voir, parce que les onze autres lignes sont justes.
 * ⚠️ LA RÈGLE : une ligne d'en-tête ne contient (presque) pas de nombres. Une ligne de données en
 * contient toujours — un prix, une quantité, un code-barres.
 */
export function ressembleAUnEntete(line: readonly string[]): boolean {
  const remplies = line.filter((c) => c.trim() !== '');
  if (remplies.length === 0) return false;
  const nombres = remplies.filter((c) => /^[-+]?[0-9][0-9\s.,]*$/.test(c.trim())).length;
  return nombres / remplies.length < 0.34;
}
